import type {
  GemXQlFilters,
  GemXQlOverrideFilters,
  GemXQlVersionIds,
  OrderByFields,
} from '../../types';

import { OPERATOR_IS, OPERATOR_IS_ONE_OF } from '../../constants';
import { EOperatorField, EFilterField } from '../../types';

export interface IBuildFilterConditionsParams {
  filters: GemXQlFilters;
  overrideFilters?: GemXQlOverrideFilters;
}

export interface IBuildVersionIdConditionParams {
  versionIds?: GemXQlVersionIds;
}

interface FilterConfig {
  operator?: EOperatorField;
  operatorVal?: string;
  singleField: EFilterField;
  multiField: EFilterField;
  condition?: EFilterField;
  fieldName?: EFilterField;
}

export const filterConfigs: FilterConfig[] = [
  {
    operator: EOperatorField.DEVICE_OPERATOR,
    singleField: EFilterField.DEVICE,
    multiField: EFilterField.DEVICES,
    fieldName: EFilterField.DEVICE,
  },
  {
    operator: EOperatorField.VISITOR_OPERATOR,
    singleField: EFilterField.VISITOR,
    multiField: EFilterField.VISITORS,
    fieldName: EFilterField.VISITOR,
  },
  {
    operator: EOperatorField.TRAFFIC_SOURCE_OPERATOR,
    singleField: EFilterField.TRAFFIC_SOURCE,
    multiField: EFilterField.TRAFFIC_SOURCES,
    fieldName: EFilterField.TRAFFIC_SOURCE,
  },
  {
    operator: EOperatorField.VERSION_OPERATOR,
    singleField: EFilterField.VERSION,
    multiField: EFilterField.VERSIONS,
    fieldName: EFilterField.VERSION,
  },
  {
    operator: EOperatorField.PAGE_OPERATOR,
    singleField: EFilterField.SINGLE_PAGE,
    multiField: EFilterField.LIST_PAGE,
    fieldName: EFilterField.SINGLE_PAGE,
  },
  {
    operator: EOperatorField.CAMPAIGN_ITEM_OPERATOR,
    singleField: EFilterField.GROUP_CAMPAIGN_ITEM,
    multiField: EFilterField.GROUP_CAMPAIGN_ITEMS,
    condition: EFilterField.GROUP_CAMPAIGN,
    fieldName: EFilterField.SINGLE_CAMPAIGN,
  },
  {
    operatorVal: OPERATOR_IS,
    singleField: EFilterField.SINGLE_CAMPAIGN,
    multiField: EFilterField.GROUP_CAMPAIGN,
  },
  {
    operatorVal: OPERATOR_IS,
    singleField: EFilterField.PRICE_CAMPAIGN,
    multiField: EFilterField.PRICE_CAMPAIGN,
    fieldName: EFilterField.SINGLE_CAMPAIGN,
  },
  {
    operatorVal: OPERATOR_IS,
    singleField: EFilterField.URL_CAMPAIGN,
    multiField: EFilterField.URL_CAMPAIGN,
    fieldName: EFilterField.SINGLE_CAMPAIGN,
  },
];

const buildCondition = (data: {
  operatorValue: string | undefined;
  singleValue?: string;
  multiValue?: string[];
  fieldName: string;
}) => {
  const { operatorValue, singleValue, multiValue, fieldName } = data;
  if (operatorValue === OPERATOR_IS && singleValue) {
    return `${fieldName} = ${singleValue}`;
  } else if (
    operatorValue === OPERATOR_IS_ONE_OF &&
    Array.isArray(multiValue) &&
    multiValue.length > 0
  ) {
    return `${fieldName} IN (${multiValue.join(', ')})`;
  }
  return '';
};

const buildOverrideFilterConditions = (overrideFilters?: GemXQlOverrideFilters) => {
  if (!overrideFilters) return {};

  return Object.fromEntries(
    Object.entries(overrideFilters)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => {
        const condition = Array.isArray(value)
          ? `${key} IN (${value.join(', ')})`
          : `${key} = ${value}`;
        return [key, condition];
      }),
  );
};

export const generateFilterConditions = (
  filters: GemXQlFilters,
  overrideFilters?: GemXQlOverrideFilters,
) => {
  const conditions: string[] = [];

  const hasFilters = Object.keys(filters).length > 0;
  const hasOverride = !!overrideFilters && Object.keys(overrideFilters).length > 0;
  if (!hasFilters && !hasOverride) return conditions;

  const overrideConditionsMap = buildOverrideFilterConditions(overrideFilters);

  filterConfigs.forEach(
    ({ operator, operatorVal, singleField, multiField, condition, fieldName }) => {
      if (overrideConditionsMap[singleField]) {
        conditions.push(overrideConditionsMap[singleField]);
        return;
      }

      const operatorValue = operator ? filters[operator] : operatorVal;
      let singleValue = filters[singleField];
      const multiValue = filters[multiField];
      const conditionValue = condition && filters[condition];

      if (condition && !conditionValue) {
        return;
      }

      const resolvedFieldName = fieldName ?? (singleValue ? singleField : multiField);

      if (operatorVal && !singleValue) {
        singleValue = filters[multiField];
      }

      const result = buildCondition({
        operatorValue,
        singleValue: Array.isArray(singleValue) ? undefined : singleValue,
        multiValue: Array.isArray(multiValue) ? multiValue : undefined,
        fieldName: resolvedFieldName,
      });
      if (result) conditions.push(result);
    },
  );

  return conditions;
};

export const buildVersionIdCondition = (versionIds?: GemXQlVersionIds) => {
  const conditions: string[] = [];

  if (!versionIds) return conditions;

  Object.entries(versionIds).forEach(([key, value]) => {
    if (!value) return;

    conditions.push(`${key} = ${value}`);
  });

  return conditions;
};

export const getFirstMetricInOrderBy = (orderBy: OrderByFields | undefined, metrics: string[]) => {
  if (!orderBy || metrics.length === 0) return [];

  const metric = metrics.find((metric) => orderBy[metric]);
  return metric ? [metric] : [];
};
